import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { SignupPlans } from "@/components/SignupPlans";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AffiliationPicker } from "@/components/AffiliationPicker";

export default function Onboarding() {
  const { user, church, setChurch } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [aff, setAff] = useState("");
  const [affName, setAffName] = useState("");
  const [ent, setEnt] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const load = useCallback(() => api.get("/billing/entitlement").then((r) => { setEnt(r.data); setLoadErr(false); }).catch(() => setLoadErr(true)), []);
  useEffect(() => { load(); }, [load]);

  if (church) return <Navigate to="/dashboard" replace />;
  if (loadErr) return <div className="text-center py-20" data-testid="onboarding-load-error"><p className="text-navy">We couldn't load your plan.</p><button onClick={load} className="btn-navy mt-4">Try again</button></div>;
  if (!ent) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" /></div>;
  if (!ent.entitlement) return <SignupPlans plans={ent.plans} onReady={load} />;

  const submit = async (e) => {
    e.preventDefault();
    if (!aff) return toast.error("Please choose your church's religious affiliation");
    setBusy(true);
    try {
      const { data } = await api.post("/church", { name, notify_email: email, affiliation: aff, affiliation_name: aff === "custom" ? affName : null });
      setChurch(data);
      toast.success("Your church is ready. Ministry teams loaded.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Could not create church");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto card-soft p-10 md:p-12 fade-up" data-testid="onboarding-card">
      <p className="eyebrow">Step 2 of 2 · Welcome, {user?.name?.split(" ")[0]}</p>
      <h1 className="font-display text-navy text-4xl font-semibold tracking-tight mt-3">Let's set up <span className="font-accent text-teal">your church.</span></h1>
      <p className="text-muted-ink mt-4">We'll create your public assessment link and pre-load ministry teams for your tradition that you can edit anytime. Your plan is active.</p>
      <form onSubmit={submit} className="mt-10 space-y-6">
        <div className="space-y-2">
          <Label htmlFor="cname">Church name</Label>
          <Input id="cname" required minLength={2} value={name} onChange={(e) => setName(e.target.value)}
            placeholder="ReStory Church" className="h-12 rounded-lg" data-testid="onboarding-church-name-input" />
        </div>
        <AffiliationPicker value={aff} onChange={setAff} />
        {aff === "custom" && <Input value={affName} onChange={(e) => setAffName(e.target.value)} maxLength={60} placeholder="Your denomination or tradition"
          className="h-12 rounded-lg" data-testid="onboarding-affiliation-name-input" />}
        <div className="space-y-2">
          <Label htmlFor="cemail">Send results to</Label>
          <Input id="cemail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="h-12 rounded-lg" data-testid="onboarding-email-input" />
          <p className="text-xs text-muted-ink">Each completed assessment is emailed here.</p>
        </div>
        <button disabled={busy} className="btn-navy w-full" data-testid="onboarding-submit-btn">
          {busy ? "Creating..." : "Create my church"}
        </button>
      </form>
    </div>
  );
}
